import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import { getCategoryLabelById } from "../../../utils/labelsById";
import useExportChartImage from "../useExportChartImage";
import SaveButton from "../UI/SaveButton";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Default input
const chartData = {
  "10-50": 0,
  "0-1": 3,
  "50-100": 0,
  "100-1000": 0,
  "1-5": 0,
  "1000-2000": 0,
};

const HorizontalBarWithTable = ({ dataChart, dataSetting, title }) => {
  const stateDataChart = dataChart || chartData;
  const chartRef = useRef();
  const exportChartImage = useExportChartImage();

  const fileName = dataSetting?.name + "_" + dataSetting?.year;

  // --- Sort ranges by their lower bound ---
  const sortedEntries = Object.entries(stateDataChart).sort(([a], [b]) => {
    const getLowerBound = (range) => {
      const match = range.match(/-?\d+\.?\d*/g); // extract numeric parts including decimals and negatives
      return parseFloat(match?.[0] ?? 0);
    };
    return getLowerBound(a) - getLowerBound(b);
  });

  const labels = sortedEntries.map(([key]) => key);
  const rawValues = sortedEntries.map(([, value]) => value);

  // --- Calculate total and percentages ---
  const total = rawValues.reduce((sum, val) => sum + val, 0) || 1;
  const values = rawValues.map((val) =>
    parseFloat(((val / total) * 100).toFixed(1))
  );

  // --- Chart Data ---
  const data = {
    labels,
    datasets: [
      {
        label: "Series1",
        data: values,
        backgroundColor: "#EC2100",
        barThickness: 20,
        datalabels: {
          anchor: "end",
          align: "right",
          color: "black",
          font: {
            size: 12,
            weight: "bold",
          },
          formatter: (value) => `${value}%`,
        },
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    layout: {
      padding: {
        right: 40, // give space for the 100% label
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      y: {
        ticks: {
          font: {
            size: 14,
          },
        },
      },
    },
  };

  // --- Export Button Handler ---
  const handleExportImage = () => {
    exportChartImage({
      chartRef,
      fileName,
      dataSetting,
    });
  };

  return (
    <div>
      <div style={{ width: "100%" }} ref={chartRef}>
        {title && (
          <h3 style={{ marginBottom: "20px" }}>
            {getCategoryLabelById(title)}
          </h3>
        )}
        <Bar data={data} options={options} />
        <table
          style={{
            marginTop: "30px",
            borderCollapse: "collapse",
            width: "100%",
            textAlign: "center",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  borderTop: "1px solid transparent",
                  borderLeft: "1px solid transparent",
                  padding: "6px",
                  backgroundColor: "white",
                }}
              ></th>
              {labels.map((label) => (
                <th
                  key={label}
                  style={{ border: "1px solid #ccc", padding: "6px" }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                <div
                  style={{
                    display: "inline-block",
                    backgroundColor: "#EC2100",
                    width: "14px",
                    height: "14px",
                    marginRight: "6px",
                    verticalAlign: "middle",
                  }}
                ></div>
                Series1
              </td>
              {values.map((val, idx) => (
                <td
                  key={idx}
                  style={{ border: "1px solid #ccc", padding: "6px" }}
                >
                  {val}%
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <SaveButton onClick={handleExportImage} />
    </div>
  );
};

export default HorizontalBarWithTable;
